import Box from "@mui/material/Box";
import InputRadioGroup from "common/components/Elements/Form/InputRadioGroup/InputRadioGroup";
import ModalActions from "common/components/Elements/Modal/ModalActions/ModalActions";
import SimpleModal from "common/components/Elements/Modal/SimpleModal/SimpleModal";
import type { ValueLabelOption } from "core/utilities/core.types";
import I18n from "i18n/en-US";
import React, { useState } from "react";
import { oppositeStrands } from "sequenceAnalyser/api/api";
import type { EntitySequence as Sequence, SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleReplaceSequences } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  sessionId: string;
  selection: SequenceSelection;
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  onReplaceSequences: HandleReplaceSequences;
}

function OppositeStrandsModal({
  open,
  onOpen,
  sessionId,
  sequences,
  alignedSequences,
  selection,
  onReplaceSequences,
}: ComponentProps) {
  // const ranAlignments = useSelector((state: ApplicationState) => state.sequenceAnalyser.ranAlignments);

  // Scope
  const scopes: ValueLabelOption[] = [
    { value: "all", label: "All" },
    { value: "selection", label: "Selection" },
  ];

  const [scope, setScope] = React.useState(selection.ids.length ? "selection" : "all");

  // Loading
  const [loading, setLoading] = useState(false);

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.OppositeStrandsModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={
        <Box mt={1}>
          <InputRadioGroup
            label={I18n.SequenceAnalyser.OppositeStrandsModal.scope}
            options={scopes}
            onChange={(value) => setScope(value)}
            value={scope}
            horizontal
          />

          <ModalActions
            applyText={I18n.SequenceAnalyser.OppositeStrandsModal.apply}
            persisting={loading}
            onApply={async () => {
              setLoading(true);

              try {
                // Switch sequences based on selection
                const selectedAlignedSequences =
                  scope === "selection"
                    ? alignedSequences.filter((s) => selection.ids.includes(s.id))
                    : alignedSequences;

                const oppositeStrandsSequences = await oppositeStrands(
                  sessionId,
                  selectedAlignedSequences.map((as) => sequences.find((s) => s.id === as.id) as Sequence),
                );

                // Replace switched sequences within existing sequences
                const newSequences = sequences.map((s) => oppositeStrandsSequences.find((ns) => ns.id === s.id) || s);

                // Re-align all sequences
                // FIXME: Disabled as an alignment takes too long
                // const newAlignedSequences = await alignSequences(
                //   alignedSequences.map((as) => newSequences.find((ns) => ns.id === as.id) as Sequence),
                //   arrayLast(ranAlignments),
                // );

                // Send back new sequences as sequence is not opposite strand and feature positions have been adjusted
                onReplaceSequences(
                  oppositeStrandsSequences,
                  oppositeStrandsSequences,
                  newSequences,
                  oppositeStrandsSequences.map((s) => s.id),
                );
              } finally {
                onOpen(false);
              }
            }}
          />
        </Box>
      }
    />
  );
}

export default OppositeStrandsModal;
